// Payment.plan über eine eigene, fehlertolerante Query.
//
// Payment.plan darf nie in src/graphql/schema/payment.js stehen. Das ist die
// Render-Gate-Query in App.js. Fehlt das Feld auf einer Stage, lehnt AppSync
// die ganze Query ab und alle Kunden hängen im Loader. Deshalb fragt diese
// Datei nur `{ id plan }` ab, und das nur auf Stages in
// PAYMENT_PLAN_FIELD_STAGES. Das Ergebnis erreicht payment-selection.js als
// planById.
//
// Ein leeres planById ist nach der Migration nicht fail-open. Das
// Plan-Payment ist dann das neueste Payment ohne erkennbaren Plan, wird zum
// legacyModePayment und bringt seine Preset-Produkte und Mengen in die alten
// Gates. Deshalb gilt bei einem Fehler der zuletzt erfolgreich geladene Stand
// des Partners (localStorage PRICING_PAYMENT_PLANS), und nur ohne jeden Stand
// ergibt ein Fehler {}.
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'

import { STAGE } from '../../helpers/stage'

// Payment.plan ist freigegeben (Backend feat/payment-plan, 2026-10-22). Die
// Query ist fehlertolerant, trotzdem gehört das Backend zuerst auf die Stage.
// Davor scheitert sie bei jedem Seitenaufruf, bis nach den Wiederholungen
// ist planFieldSettled false und die Talentliste maskiert, danach meldet sie
// an Bugsnag. Bevor CS in einer Stage das erste Paket anlegt, müssen Backend
// und dieser Stand dort live sein.
//
// Austragen ist kein Rollback, sobald Kunden migriert sind. Ohne Plan-Feld ist
// das Plan-Payment das neueste Payment und gilt als legacy, seine
// Preset-Produkte und Mengen sperren dann die alten Gates. Zurück auf das
// Altpayment geht es nur über den Schalter pricing_enforcement, mit
// eingetragener Stage.
export const PAYMENT_PLAN_FIELD_STAGES = Object.freeze([
  'develop',
  'staging',
  'production'
])

export const isPaymentPlanFieldAvailable = (
  stage = STAGE,
  fieldStages = PAYMENT_PLAN_FIELD_STAGES
) => !!stage && fieldStages.includes(stage)

export const paymentPlansByPartnerIdByCreatedAt = /* GraphQL */ `
  query PaymentPlansByPartnerIdByCreatedAt(
    $partner_id: ID!
    $limit: Int
    $nextToken: String
  ) {
    paymentByPartnerIdByCreatedAt(
      partner_id: $partner_id
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        plan
      }
      nextToken
    }
  }
`

// Ändert sich ein Payment (neues Payment, Plan im OS gesetzt), ändert sich
// updatedAt und damit der Key. Deshalb reicht staleTime Infinity.
export const paymentsFingerprint = (payments) =>
  (payments || [])
    .filter(Boolean)
    .map(({ id, updatedAt }) => `${id}:${updatedAt || ''}`)
    .sort()
    .join('|')

export const paymentPlansQueryKey = (partnerId, fingerprint) => [
  'payment-plans',
  partnerId,
  fingerprint
]

const PAGE_SIZE = 200
const EMPTY = Object.freeze({})

// Drei Wiederholungen nach 0,5, 1 und 2 Sekunden. Solange sie laufen, ist
// planFieldSettled false und die Talentliste maskiert vorläufig, deshalb
// kürzer als der globale Default.
export const PLAN_FETCH_RETRIES = 3
export const planRetryDelay = (failureCount) =>
  Math.min(500 * 2 ** failureCount, 2000)

// Zuletzt erfolgreich geladene Pläne je Partner, damit ein Fehler nicht auf
// {} fällt. localStorage statt nur Query-Cache, denn der persistierte Cache
// verfällt mit jedem Release (buster). signOut leert localStorage und damit
// auch das. Der Key steht hier und nicht in LOCALSTORAGE_ITEMS, weil
// helpers/index.js aws-amplify zieht und Jest bricht.
export const PRICING_PAYMENT_PLANS = 'PRICING_PAYMENT_PLANS'

// Admins wechseln zwischen vielen Partnern, gespeichert werden nur die
// zuletzt geladenen
const STORED_PARTNER_LIMIT = 20

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const isPlanMap = (value) =>
  isPlainObject(value) &&
  Object.values(value).every((plan) => typeof plan === 'string')

const readStoredRaw = () => {
  try {
    return window.localStorage.getItem(PRICING_PAYMENT_PLANS)
  } catch {
    return null
  }
}

// Gecacht über den Rohstring, damit planById zwischen Renders dasselbe Objekt
// bleibt und der Memo in payment-context.js nicht bei jedem Render neu rechnet
let cachedRaw
let cachedStored = EMPTY

const parseStored = (raw) => {
  if (raw === cachedRaw) return cachedStored
  let parsed = null
  try {
    parsed = raw ? JSON.parse(raw) : null
  } catch {
    parsed = null
  }
  cachedRaw = raw
  cachedStored = isPlainObject(parsed) ? parsed : EMPTY
  return cachedStored
}

/** Zuletzt gespeicherte Pläne des Partners oder null */
export const readStoredPaymentPlans = (partnerId) => {
  if (!partnerId) return null
  const entry = parseStored(readStoredRaw())[partnerId]
  return isPlanMap(entry) ? entry : null
}

export const storePaymentPlans = (partnerId, planById) => {
  if (!partnerId || !isPlanMap(planById)) return
  const stored = parseStored(readStoredRaw())
  if (JSON.stringify(stored[partnerId]) === JSON.stringify(planById)) return

  const others = Object.entries(stored)
    .filter(([id]) => id !== partnerId)
    .slice(-(STORED_PARTNER_LIMIT - 1))

  try {
    window.localStorage.setItem(
      PRICING_PAYMENT_PLANS,
      JSON.stringify(Object.fromEntries([...others, [partnerId, planById]]))
    )
  } catch {
    // Ohne localStorage gibt es nur keinen Stand für den Fehlerfall
  }
}

let reportedFailure = false
let reportedPartial = false

// Fehler am plan eines einzelnen Items, etwa ein Wert außerhalb des Enums
// PAYMENT_PLAN. AppSync setzt dort null und liefert die übrigen Items.
const isItemPlanError = (error) => {
  const path = error?.path
  return (
    Array.isArray(path) &&
    path.length === 4 &&
    path[0] === 'paymentByPartnerIdByCreatedAt' &&
    path[1] === 'items' &&
    typeof path[2] === 'number' &&
    path[3] === 'plan'
  )
}

// Amplify rejected bei jedem Fehler, auch mit Teildaten. Betreffen alle
// Fehler nur den plan einzelner Items, gilt die Seite. Diese Payments haben
// dann keinen Plan und gelten als legacy, genau wie ein unbekannter Wert.
// Sonst bliebe ein einziges kaputtes Payment für den ganzen Partner ohne
// Pläne. Jeder andere Fehler wirft weiter.
const requestPage = async (API, variables) => {
  try {
    return await API.graphql({
      query: paymentPlansByPartnerIdByCreatedAt,
      variables
    })
  } catch (err) {
    const errors = err?.errors
    const partial =
      !!err?.data?.paymentByPartnerIdByCreatedAt &&
      Array.isArray(errors) &&
      errors.length > 0 &&
      errors.every(isItemPlanError)
    if (!partial) throw err

    if (!reportedPartial) {
      reportedPartial = true
      console.error(
        new Error(
          `paymentByPartnerIdByCreatedAt plan teilweise: ${errors[0]?.message || 'unknown'}`
        )
      )
    }
    return err
  }
}

/**
 * Alle Pläne der Payments eines Partners als `{ [paymentId]: plan }`. Nur
 * Payments mit Plan-Wert stehen drin. Wirft bei jedem Fehler außer einem
 * Fehler am plan einzelner Items (requestPage).
 *
 * Direkt über API.graphql statt über query(), weil der Wrapper jeden Fehler
 * an Bugsnag meldet. aws-amplify wird erst beim Aufruf geladen, damit Jest
 * diese Datei ohne Mock importieren kann.
 */
export const fetchPaymentPlans = async (partnerId) => {
  const { API } = await import('aws-amplify')
  const planById = {}
  let nextToken = null

  do {
    const res = await requestPage(API, {
      partner_id: partnerId,
      limit: PAGE_SIZE,
      nextToken
    })
    const page = res?.data?.paymentByPartnerIdByCreatedAt
    if (!page) throw new Error('keine Daten')

    for (const item of page.items || []) {
      if (item?.id && item.plan !== null && item.plan !== undefined) {
        planById[item.id] = item.plan
      }
    }
    nextToken = page.nextToken || null
  } while (nextToken)

  return planById
}

// Höchstens eine Meldung pro Seitenaufruf, erst nach allen Wiederholungen.
// console.error mit einer Error-Instanz geht über src/index.js an Bugsnag,
// genau wie notifyBugsnag, nur ohne helpers/index.js.
const reportFailure = (err) => {
  if (reportedFailure) return
  reportedFailure = true
  console.error(
    new Error(
      `paymentByPartnerIdByCreatedAt plan: ${err?.errors?.[0]?.message || err?.message || 'unknown'}`
    )
  )
}

/**
 * Pläne der Payments als planById für resolveEntitlements.
 *
 * Bis zur ersten Antwort für den aktuellen Key steht der Stand des vorigen
 * Keys desselben Partners als Platzhalter in `data`, sonst der gespeicherte
 * Stand. `settled` ist dann false.
 *
 * Ein Fehler kommt erst nach PLAN_FETCH_RETRIES Wiederholungen an. Dann gilt
 * der letzte Stand dieses Keys (fehlgeschlagenes Neuladen), sonst der
 * gespeicherte Stand des Partners. Nur ohne jeden Stand ist `data` `{}` und
 * `failed` true, alle Payments gelten dann als legacy. Eine fehlgeschlagene
 * Query ohne Daten ist stale und lädt beim nächsten Fokus des Tabs neu. Ein
 * Fehler wird nicht persistiert.
 *
 * `settled` ist true, wenn die Query nicht läuft (Stage nicht freigeschaltet,
 * kein Partner) oder für den aktuellen Key abgeschlossen ist, auch mit Fehler.
 *
 * `stage`, `fieldStages` und `retryDelay` sind nur für Tests da.
 *
 * @returns {{ data: Object<string, string>, settled: boolean,
 *   failed: boolean }}
 */
export const usePaymentPlans = ({
  partnerId,
  payments,
  stage = STAGE,
  fieldStages = PAYMENT_PLAN_FIELD_STAGES,
  retryDelay = planRetryDelay
} = {}) => {
  const enabled = !!partnerId && isPaymentPlanFieldAvailable(stage, fieldStages)
  const fingerprint = useMemo(() => paymentsFingerprint(payments), [payments])

  // Den vorigen Key nur übernehmen, wenn er zum selben Partner gehört. Ein
  // Admin, der den Partner wechselt, sieht sonst kurz fremde Pläne.
  const placeholderData = useCallback(
    (previous, previousQuery) =>
      previous !== undefined && previousQuery?.queryKey?.[1] === partnerId
        ? previous
        : (readStoredPaymentPlans(partnerId) ?? undefined),
    [partnerId]
  )

  const { data, error, isError, isFetched, isPlaceholderData, isSuccess } =
    useQuery({
      queryKey: paymentPlansQueryKey(partnerId, fingerprint),
      queryFn: () => fetchPaymentPlans(partnerId),
      enabled,
      placeholderData,
      // Erfolgreiche Daten werden nie stale, ein neues oder geändertes Payment
      // ändert den Key. Fokus lädt deshalb nur eine fehlgeschlagene Query neu.
      staleTime: Infinity,
      retry: PLAN_FETCH_RETRIES,
      retryDelay
    })

  useEffect(() => {
    if (enabled && isSuccess && !isPlaceholderData && data) {
      storePaymentPlans(partnerId, data)
    }
  }, [enabled, isSuccess, isPlaceholderData, data, partnerId])

  useEffect(() => {
    if (enabled && error) reportFailure(error)
  }, [enabled, error])

  let planById = EMPTY
  let failed = false
  if (enabled && data) {
    planById = data
  } else if (enabled && isError) {
    const stored = readStoredPaymentPlans(partnerId)
    if (stored) planById = stored
    else failed = true
  }
  const settled = !enabled || isFetched

  return useMemo(
    () => ({ data: planById, settled, failed }),
    [planById, settled, failed]
  )
}

// Nur für Tests
export const resetPaymentPlansForTests = () => {
  reportedFailure = false
  reportedPartial = false
  cachedRaw = undefined
  cachedStored = EMPTY
}
